/**
 * Affichage d'un quiz généré — design Carbon
 */

import { useState } from 'react'
import {
  Tile,
  Layer,
  Tag,
  RadioButtonGroup,
  RadioButton,
  Button,
} from '@carbon/react'
import {
  CheckmarkFilled,
  CloseFilled,
  ArrowLeft,
  ArrowRight,
  Download,
  Renew,
} from '@carbon/icons-react'
import type { QuizSchema } from '@sphere/ai/domain'
import styles from './QuizDisplay.module.scss'

interface QuizDisplayProps {
  quiz: QuizSchema
  onDownload?: () => void
  onRestart?: () => void
  className?: string
}

interface QuizState {
  currentQuestion: number
  answers: Record<number, string>
  showResults: boolean
  score: number
}

export function QuizDisplay({
  quiz,
  onDownload,
  onRestart,
  className = '',
}: QuizDisplayProps) {
  const [quizState, setQuizState] = useState<QuizState>({
    currentQuestion: 0,
    answers: {},
    showResults: false,
    score: 0,
  })

  const currentQuestion = quiz.questions[quizState.currentQuestion]
  const isLastQuestion = quizState.currentQuestion === quiz.questions.length - 1
  const hasAnswer = quizState.answers[quizState.currentQuestion] !== undefined

  if (!currentQuestion) {
    return (
      <Layer className={`${styles.card} ${className}`}>
        <Tile className={`${styles.tile} ${styles.errorCard}`}>
          <p className={styles.errorText}>Erreur : question invalide</p>
        </Tile>
      </Layer>
    )
  }

  const handleAnswerSelect = (answer: string) => {
    setQuizState(prev => ({
      ...prev,
      answers: { ...prev.answers, [prev.currentQuestion]: answer },
    }))
  }

  const handleNext = () => {
    if (isLastQuestion) {
      const score = quiz.questions.reduce(
        (acc, q, i) => acc + (quizState.answers[i] === q.answer ? 1 : 0),
        0
      )
      setQuizState(prev => ({ ...prev, showResults: true, score }))
    } else {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
      }))
    }
  }

  const handlePrevious = () => {
    setQuizState(prev => ({
      ...prev,
      currentQuestion: Math.max(0, prev.currentQuestion - 1),
    }))
  }

  const handleRestart = () => {
    setQuizState({
      currentQuestion: 0,
      answers: {},
      showResults: false,
      score: 0,
    })
  }

  const progress =
    ((quizState.currentQuestion + 1) / quiz.questions.length) * 100

  // Results view
  if (quizState.showResults) {
    const percentage = Math.round(
      (quizState.score / quiz.questions.length) * 100
    )
    const scoreClass =
      percentage >= 80
        ? styles.scoreGood
        : percentage >= 60
          ? styles.scoreMedium
          : styles.scoreLow

    return (
      <Layer className={`${styles.card} ${className}`}>
        <Tile className={styles.tile}>
          <div className={styles.resultsHeader}>
            <h2 className={styles.resultsTitle}>Résultats du Quiz</h2>
            <div className={`${styles.resultsScore} ${scoreClass}`}>
              {percentage}%
            </div>
            <p className={styles.resultsSub}>
              {quizState.score} / {quiz.questions.length} réponses correctes
            </p>
          </div>

          <div className={styles.resultsList}>
            {quiz.questions.map((q, i) => {
              const userAnswer = quizState.answers[i]
              const isCorrect = userAnswer === q.answer
              return (
                <div
                  key={i}
                  className={`${styles.resultItem} ${isCorrect ? styles.correct : styles.incorrect}`}
                >
                  <div className={styles.resultItemIcon}>
                    {isCorrect ? (
                      <CheckmarkFilled
                        size={20}
                        className={styles.iconCorrect}
                      />
                    ) : (
                      <CloseFilled size={20} className={styles.iconIncorrect} />
                    )}
                  </div>
                  <div className={styles.resultItemContent}>
                    <p className={styles.resultItemTitle}>Question {i + 1}</p>
                    <p className={styles.resultItemText}>
                      Votre réponse : {userAnswer}
                    </p>
                    {!isCorrect && (
                      <p
                        className={`${styles.resultItemText} ${styles.correctAnswer}`}
                      >
                        Bonne réponse : {q.answer}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className={styles.resultsActions}>
            <Button kind="tertiary" renderIcon={Renew} onClick={handleRestart}>
              Recommencer
            </Button>
            {onDownload && (
              <Button
                kind="tertiary"
                renderIcon={Download}
                onClick={onDownload}
              >
                Télécharger
              </Button>
            )}
            {onRestart && (
              <Button kind="primary" onClick={onRestart}>
                Nouveau Quiz
              </Button>
            )}
          </div>
        </Tile>
      </Layer>
    )
  }

  // Question view
  return (
    <Layer className={`${styles.card} ${className}`}>
      <Tile className={styles.tile}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{quiz.title}</h1>
            <div className={styles.tags}>
              {quiz.metadata?.difficulty && (
                <Tag type="blue">{quiz.metadata.difficulty}</Tag>
              )}
              {quiz.metadata?.estimated_time && (
                <Tag type="outline">{quiz.metadata.estimated_time} min</Tag>
              )}
            </div>
          </div>
          {quiz.description && (
            <p className={styles.description}>{quiz.description}</p>
          )}

          <div className={styles.progressBar}>
            <div className={styles.progressInfo}>
              <span>
                Question {quizState.currentQuestion + 1} sur{' '}
                {quiz.questions.length}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className={styles.progressTrack}>
              <div
                className={styles.progressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <div className={styles.questionBlock}>
          <div className={styles.questionHeader}>
            <span className={styles.questionNumber}>
              {quizState.currentQuestion + 1}
            </span>
            <h3 className={styles.questionText}>{currentQuestion.question}</h3>
          </div>

          <RadioButtonGroup
            name={`quiz-question-${quizState.currentQuestion}`}
            valueSelected={quizState.answers[quizState.currentQuestion] ?? ''}
            onChange={v => handleAnswerSelect(String(v ?? ''))}
            orientation="vertical"
            className={styles.choices}
          >
            {currentQuestion.choices.map((option, i) => {
              const label =
                typeof option === 'string' ? option : (option as any).text
              const value =
                typeof option === 'string'
                  ? option
                  : (option as any).id || (option as any).text
              return (
                <RadioButton
                  key={i}
                  id={`q${quizState.currentQuestion}-opt-${i}`}
                  value={value}
                  labelText={label}
                />
              )
            })}
          </RadioButtonGroup>
        </div>

        <div className={styles.nav}>
          <Button
            kind="tertiary"
            renderIcon={ArrowLeft}
            onClick={handlePrevious}
            disabled={quizState.currentQuestion === 0}
          >
            Précédent
          </Button>
          <Button
            kind="primary"
            renderIcon={ArrowRight}
            onClick={handleNext}
            disabled={!hasAnswer}
          >
            {isLastQuestion ? 'Terminer' : 'Suivant'}
          </Button>
        </div>
      </Tile>
    </Layer>
  )
}
